import { useState, useEffect, useRef, useCallback } from 'react';

interface SprintVelocityProps {
  sprintVelocity: number[];
}

export default function SprintVelocity(props: SprintVelocityProps) {
  const [hovered, setHovered] = useState(false);
  const [averageVelocity, setAverageVelocity] = useState<number>(15);
  const chartRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // Calculate the average velocity (last 3 sprints or fewer if less than 3)
    if (props.sprintVelocity.length === 0) {
      setAverageVelocity(15); // Default if no sprints yet
    } else {
      const lastThreeSprints = props.sprintVelocity.slice(-3);
      const sum = lastThreeSprints.reduce((acc, velocity) => acc + velocity, 0);
      const average = sum / lastThreeSprints.length;
      setAverageVelocity(average);
    }
  }, [props.sprintVelocity]);

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const drawChart = useCallback(() => {
    const canvas = chartRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const chartWidth = 400;
        const chartHeight = 200;
        const padding = 20;
        const barWidth = 25; // width of each bar
        const barSpacing = 10; // space between bars

        // Clear previous chart
        ctx.clearRect(0, 0, chartWidth, chartHeight);

        // Draw the title at the top of the chart
        ctx.fillStyle = '#000';
        ctx.font = '14px Arial';
        ctx.fillText('Sprint Velocity Chart', chartWidth / 2 - 80, padding); // Title in the center

        // Find the minimum and maximum values to color bars
        const minVelocity = Math.min(...props.sprintVelocity);
        const maxVelocity = Math.max(...props.sprintVelocity);

        // Draw bars for each sprint
        props.sprintVelocity.forEach((velocity, index) => {
          const x = index * (barWidth + barSpacing) + padding;
          const y = chartHeight - velocity * 5 - padding;
          const height = velocity * 5; // scale the height for visual size

          // Color the bars based on the value
          if (velocity === minVelocity) {
            ctx.fillStyle = 'red'; // Lowest bar
          } else if (velocity === maxVelocity) {
            ctx.fillStyle = 'green'; // Highest bar
          } else {
            ctx.fillStyle = '#36a2eb'; // Other bars
          }

          ctx.fillRect(x, y, barWidth, height);

          // Add story points label on top of the bar
          ctx.fillStyle = '#000'; // Label color
          ctx.font = '12px Arial';
          ctx.fillText(`${velocity}`, x + barWidth / 2 - 10, y - 5); // Position the label above the bar
        });

        // Draw X-axis labels (Sprint numbers)
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        props.sprintVelocity.forEach((_, index) => {
          const x = index * (barWidth + barSpacing) + padding;
          const y = chartHeight - padding / 2;
          ctx.fillText(`${index + 1}`, x + barWidth / 2 - 5, y); // Position the sprint number below each bar
        });
      }
    }
  }, [props.sprintVelocity]);

  useEffect(() => {
    if (hovered) {
      drawChart(); // Only draw the chart when hovered
    }
  }, [hovered, props.sprintVelocity, drawChart]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <p>{props.sprintVelocity.length === 0 ? <strong>Estimated Velocity: </strong> : <strong>Current Velocity: </strong>}{Math.round(averageVelocity)}</p>

        {hovered && (
          <canvas
            ref={chartRef}
            width={400}
            height={200}
            style={{
              position: 'absolute',
              top: '40px',
              left: '0',
              zIndex: 1,
              backgroundColor: '#fff',
              border: '1px solid #000',
            }}
          />
        )}
      </div>
    </div>
  );
}
